import React, { useEffect, useRef, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { getInsuranceTypeBEName } from '../../../../../../itrust_common/components/HelperFunctions'
import { orderLoad } from '../../../../../../redux/actions/order/orderActions'
import { getFormValues, isDirty, submit } from 'redux-form'
import { getInsuranceFromRPAServices, isRpaVerifiedInsurance } from '../../../other/AddOnsHelperFunctions'
import { RenderRPAService } from './RenderRPAServiceButton'
import { visionInsuranceDetailView, visionInsuranceEligibilityLogLoad, visionInsuranceEligibilityRequirementsLoad, visionInsuranceEligibilityReset, visionInsuranceEligibilityVerify } from '../../../../../../redux/actions/addOns/rpa/vision_insurance/visionInsuranceAction'
import { eligibilityProcessingKey } from '../../../../../../redux/reducers/addOns/rpa/vision_insurance/visionInsuranceReducer'
import { patientLoad } from '../../../../../../redux/actions/patient/patientActions'
import useInsuranceMappedFailed from '../useInsuranceMappedFailed'

const ELIGIBILITY_POLL_INTERVAL_MS = 3000

const hasLastFourDigits = (value) => String(value || '').replace(/\D/g, '').length >= 4

const currentValue = (formValue, savedValue) => formValue !== undefined ? formValue : savedValue

const filledInForm = (field, { memberId, patient, patientFormValues }) => {
  const firstName = currentValue(patientFormValues?.first_name, patient?.first_name)
  const lastName = currentValue(patientFormValues?.last_name, patient?.last_name)
  const dateOfBirth = currentValue(patientFormValues?.date_of_birth, patient?.date_of_birth)
  const zip = currentValue(patientFormValues?.home_address_attributes?.zip, patient?.home_address_attributes?.zip)
  const ssn = currentValue(patientFormValues?.ssn, patient?.ssn)

  switch (field.key) {
    case 'member_id_or_ssn_last_four':
      return hasLastFourDigits(memberId) || hasLastFourDigits(ssn)
    case 'first_initial':
    case 'first_name':
      return String(firstName || '').trim().length > 0
    case 'last_name':
      return String(lastName || '').trim().length > 0
    case 'date_of_birth':
      return !!dateOfBirth
    case 'zip':
      return String(zip || '').trim().length > 0
    default:
      return false
  }
}

function RPAInsuranceVerify(props) {
  const { patientId, orderId, patient, selectedInsuranceName, selectedInsuranceMemberId, currentStoreId, insuranceType, services, isOrderView, isPatientView, isPatientFormDirty, patientFormValues, visionInsuranceConfig, order } = props
  const [insuranceVerifying, setInsuranceVerifying] = useState(false)
  const [showVerifyBtn, setShowVerifyBtn] = useState(true)
  const [requirementsLoading, setRequirementsLoading] = useState(false)
  const [missingFields, setMissingFields] = useState([])
  const [requirementsError, setRequirementsError] = useState('')
  const dispatch = useDispatch()
  const eligibilityPollRef = useRef(null)
  const eligibilityPollGeneration = useRef(0)
  const requirementsRequestGeneration = useRef(0)
  const startedPollRef = useRef(false)
  const mountedRef = useRef(true)
  const processingPatientId = patientId || patient?.id
  const processingKey = eligibilityProcessingKey(processingPatientId, insuranceType)
  const processingKeyRef = useRef(processingKey)
  const seenProcessingKeyRef = useRef(processingKey)
  processingKeyRef.current = processingKey

  const stopEligibilityPoll = () => {
    eligibilityPollGeneration.current += 1
    if (eligibilityPollRef.current) {
      clearInterval(eligibilityPollRef.current)
      eligibilityPollRef.current = null
    }
  }

  const { rpaVerificationFailed } = useInsuranceMappedFailed(selectedInsuranceName)
  const rpaVerifiedInsurance = isRpaVerifiedInsurance(visionInsuranceConfig, services, selectedInsuranceName)

  useEffect(() => {
    mountedRef.current = true
    // Order Patient Load
    patientId && dispatch(patientLoad(patientId, { include: '*' }))
    return () => {
      mountedRef.current = false
      stopEligibilityPoll()
      dispatch(visionInsuranceEligibilityReset())
    }
  }, [])

  useEffect(() => {
    stopEligibilityPoll()
    dispatch(visionInsuranceEligibilityReset())
  }, [orderId])

  useEffect(() => {
    if (!selectedInsuranceName) return
    stopEligibilityPoll()
    dispatch(visionInsuranceEligibilityReset())
  }, [selectedInsuranceName])

  const selectedRpaInsurance = getInsuranceFromRPAServices(services, selectedInsuranceName)
  const requirementsPatientId = isOrderView ? patientId : patient?.id

  useEffect(() => {
    const generation = ++requirementsRequestGeneration.current
    const service = selectedRpaInsurance?.config?.service

    setMissingFields([])
    setRequirementsError('')
    if (!requirementsPatientId || !['vba', 'nva', 'cec'].includes(service)) {
      setRequirementsLoading(false)
      return
    }

    setRequirementsLoading(true)
    dispatch(visionInsuranceEligibilityRequirementsLoad({
      patient_id: requirementsPatientId,
      insurance_type: getInsuranceTypeBEName(insuranceType),
      add_ons_id: selectedRpaInsurance.id
    })).then((res) => {
      if (generation !== requirementsRequestGeneration.current) return
      setMissingFields(res.value?.data?.missing_fields || [])
    }).catch(() => {
      if (generation !== requirementsRequestGeneration.current) return
      setRequirementsError('Unable to check required patient information.')
    }).finally(() => {
      if (generation === requirementsRequestGeneration.current) setRequirementsLoading(false)
    })

    return () => {
      requirementsRequestGeneration.current += 1
    }
  }, [requirementsPatientId, selectedRpaInsurance?.id, insuranceType, patient])

  const getData = (data) => {
    // default structure
    let storeId = currentStoreId ?? (orderId && order.store_id)
    const insurance = getInsuranceFromRPAServices(services, selectedInsuranceName)
    data = { ...data, patient_id: patient.id, add_ons_id: insurance.id, insurance_payer: selectedInsuranceName, service: insurance.config.service, store_id: storeId, insurance_type: getInsuranceTypeBEName(insuranceType), other_details: { resource: "verify_eligibility", member_id: data.member_id } }
    // structure as per Order Verification
    data = { ...data, orders_order_id: isOrderView ? orderId : null }
    return data
  }

  const handleBeforeVerify = (data) => {
    if (isPatientView && isPatientFormDirty) {
      dispatch(submit("patientForm"))
      setTimeout(() => {
        let invalidInputs = document.getElementsByClassName("is-invalid")
        invalidInputs.length === 0 && handleVerify(data)
      }, 500)
    }
    else {
      handleVerify(data)
    }
  }

  const handleInsuranceVerifySubmit = (data) => {
    data = getData(data)
    handleBeforeVerify(data)
  }

  const finishEligibility = (log, key) => {
    stopEligibilityPoll()
    dispatch({ type: 'VISION_INSURANCE_ELIGIBILITY_PROCESSING_DONE', data: { processingKey: key } })
    if (!mountedRef.current || processingKeyRef.current !== key) return
    setInsuranceVerifying(false)
    setShowVerifyBtn(true)
    if (log) dispatch({ type: 'VISION_INSURANCE_ELIGIBILITY_POLL_RESULT', data: log })
    if (log?.eligibility_status?.status === 'found' || log?.eligibility_file_url) {
      dispatch(visionInsuranceDetailView({ visible: true, file_url: log.eligibility_file_url, service: log.service, id: log.id }))
    }
    handleLoadUpdatedLogs()
  }

  const pollEligibility = (logId, key) => {
    stopEligibilityPoll()
    const generation = eligibilityPollGeneration.current
    let inFlight = false
    const checkStatus = () => {
      if (generation !== eligibilityPollGeneration.current || inFlight) return
      inFlight = true
      dispatch(visionInsuranceEligibilityLogLoad(logId)).then((res) => {
        if (generation !== eligibilityPollGeneration.current) return
        const log = res.value.data.rpa_vision_insurance_log
        const status = log?.eligibility_status?.status
        if (status === 'processing') return
        finishEligibility(log, key)
      }).catch(() => {
        // A failed status check leaves the row on Processing and retries on the next poll.
      }).finally(() => {
        inFlight = false
      })
    }
    checkStatus()
    eligibilityPollRef.current = setInterval(checkStatus, ELIGIBILITY_POLL_INTERVAL_MS)
  }

  useEffect(() => {
    if (seenProcessingKeyRef.current === processingKey) return
    seenProcessingKeyRef.current = processingKey
    stopEligibilityPoll()
    startedPollRef.current = false
    setInsuranceVerifying(false)
    setShowVerifyBtn(true)
  }, [processingKey])

  useEffect(() => {
    const logId = props.eligibilityProcessing?.[processingKey]
    if (!processingKey || startedPollRef.current || typeof logId === 'boolean' || logId == null || logId === '') return
    startedPollRef.current = true
    setInsuranceVerifying(insuranceType)
    setShowVerifyBtn(false)
    pollEligibility(logId, processingKey)
  }, [props.eligibilityProcessing, processingKey, insuranceType])

  const handleVerify = (data) => {
    const key = eligibilityProcessingKey(patientId || patient?.id, insuranceType)
    startedPollRef.current = true
    setInsuranceVerifying(insuranceType)
    setShowVerifyBtn(false)
    dispatch({ type: 'VISION_INSURANCE_ELIGIBILITY_PROCESSING', data: { processingKey: key } })
    dispatch(visionInsuranceEligibilityVerify(data)).then((res) => {
      res = res.value.data
      const log = res.rpa_vision_insurance_log
      if (log?.eligibility_status?.status === 'processing') {
        dispatch({ type: 'VISION_INSURANCE_ELIGIBILITY_PROCESSING', data: { processingKey: key, logId: log.id } })
        if (mountedRef.current && processingKeyRef.current === key) pollEligibility(log.id, key)
        else startedPollRef.current = false
      } else if (mountedRef.current && processingKeyRef.current === key) {
        finishEligibility(log, key)
      } else {
        dispatch({ type: 'VISION_INSURANCE_ELIGIBILITY_PROCESSING_DONE', data: { processingKey: key } })
      }
    }).catch(() => {
      startedPollRef.current = false
      if (mountedRef.current && processingKeyRef.current === key) {
        setInsuranceVerifying(false)
        setShowVerifyBtn(true)
      }
      dispatch({ type: 'VISION_INSURANCE_ELIGIBILITY_PROCESSING_DONE', data: { processingKey: key } })
    })
  }

  const handleLoadUpdatedLogs = () => {
    // load to get verify new logs
    isOrderView && dispatch(orderLoad(orderId, { include: '*' }));
    isPatientView && dispatch(patientLoad(patient.id, { include: '*' }));
  }

  return (
    <>
      {(rpaVerifiedInsurance || rpaVerificationFailed) && <RenderRPAService pprops={{
        ...props,
        handleInsuranceVerifySubmit,
        insuranceVerifying,
        showVerifyBtn,
        setShowVerifyBtn,
        rpaVerificationFailed,
        requirementsLoading,
        requirementsError,
        missingFields: missingFields.filter(field => !filledInForm(field, {
          memberId: selectedInsuranceMemberId,
          patient,
          patientFormValues
        }))
      }} />}
    </>
  )
}

const mapStateToProps = (state) => {
  const { patient: { patient }, currentStore: { store }, visionInsurance, organisation: { organisation } } = state

  return {
    patient,
    currentStoreId: store.id,
    services: visionInsurance.services,
    isPatientFormDirty: isDirty('patientForm')(state),
    patientFormValues: getFormValues('patientForm')(state),
    visionInsuranceConfig: organisation.add_ons_config?.type?.vision_insurance,
    eligibilityProcessing: visionInsurance.eligibilityProcessing || {},
  }
}

export default connect(mapStateToProps)(RPAInsuranceVerify);